/**
 * @class MultiSelectItem - holds label, checkbox and mirror view
 */
export class MultiSelectItem {
  /**
   * @constructor
   * @parma {HTMLLabelElement} label - <label> element
   * @param {HTMLInputElement} checkbox - input[type="checkbox"] element
   * @param {HTMLDivElement} mirror_view - <div> used to mirror the selected item
   */
  constructor(label, checkbox, mirror_view) {
    if (!label || !checkbox || !mirror_view) {
      throw new Error('checkbox and label and mirror view elements are required');
    }

    if (!(label instanceof HTMLLabelElement)) {
      throw new Error('label element is required');
    }

    if (!(checkbox instanceof HTMLInputElement)) {
      throw new Error('input[type="checkbox"] element is required');
    }

    if (checkbox.type !== 'checkbox') {
      throw new Error('input[type="checkbox"] element is required');
    }

    if (!(mirror_view instanceof HTMLElement)) {
      throw new Error('mirror view div element is required');
    }

    this.label = label;
    this.checkbox = checkbox;
    this.mirror_view = mirror_view;
    this.mirrored_button = this.mirror();

    // add checkbox handler
    this.checkbox.addEventListener('change', () => this.onChange());

    // execute the checkbox handler on back and forth browser navigation
    window.addEventListener('popstate', () => this.onChange());

    // execute the checkbox handler on load/init
    this.onChange();
  }

  /**
   * @public
   * @return {Boolean} result
   */
  isSelected() {
    return this.checkbox.checked;
  }

  /**
   * @private
   * on checkbox change event handler
   */
  onChange(event) {
    if (this.isSelected()) {
      this.mirror_view.append(this.mirrored_button);
    }
    else {
      this.mirrored_button.remove();
    }
  }

  /**
   * @private
   * creates a "button" like <span>${this.label.innerText} <label for="${this.label.getAttribute('for')}">×</label></span>
   * @return {HTMLElement} span
   */
  mirror() {
    const span = document.createElement('span');
    const inner_span = document.createElement('span');
    const label = document.createElement('label');

    // use `textContent` instead of `innerText`
    // this is caused by the change in chromium 96/97
    // hidden content in the <details> element is now hidden with `content-visibility: hidden` instead of `display: none`
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1276028
    inner_span.innerText = this.label.textContent;

    label.innerText = '×';
    label.setAttribute('for', this.label.getAttribute('for'));

    span.append(inner_span);
    span.append(label);

    span.classList.add('dsc-multi-select-item-mirror');

    return span;
  }
}
